/**
 * trackEvent — wrapper defensivo alrededor de gtag para eventos GA4.
 *
 * Card KEQz7YBQ (métricas de abandono del wizard). Este helper es la
 * puerta única de todos los eventos GA4 emitidos desde la SPA.
 *
 * Diseño (spec padre apI97mhv — analítica en 2 niveles):
 *
 *   - **Sin PII**: los ``params`` NUNCA deben incluir email, nombre,
 *     teléfono, texto libre del usuario, ni cualquier valor que permita
 *     identificarlo. Solo IDs opacos (``session_id``) y categorías enum
 *     (``value_kind``, ``profile``, ``step_id`` predefinido).
 *
 *   - **Silent no-op**: si ``window.gtag`` no existe (GA no cargó, un
 *     ad-blocker lo bloqueó, SSR/jsdom sin script tag) la función retorna
 *     sin efecto. La analítica NUNCA debe romper la UI del usuario, y el
 *     wizard debe seguir funcionando con o sin GA4 activo.
 *
 *   - **Reusable**: este helper es compartido — la card hermana
 *     ``N52QMDjD`` (auth_gate tracking) también lo va a consumir. Por eso
 *     vive en ``apps/web/src/analytics/`` en vez de dentro del wizard.
 *
 * En dev logueamos un warning si gtag falla, para que el desarrollador
 * vea el problema; en prod tragamos el error silenciosamente.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Emite un evento GA4 vía ``window.gtag('event', name, params)``.
 *
 * @param name   nombre del evento (ej. ``wizard_started``). Se recomienda
 *               snake_case y prefijo por dominio (``wizard_*``, ``auth_*``).
 * @param params objeto de parámetros opcional. Solo IDs opacos y enums —
 *               NUNCA texto libre del usuario ni PII.
 */
export function trackEvent(
  name: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  try {
    window.gtag('event', name, params ?? {});
  } catch (err) {
    // Silent — analytics no debe romper la UI. Log a consola sólo en dev.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[analytics] trackEvent failed', name, err);
    }
  }
}
